import * as Sentry from '@sentry/browser';
import { gql } from 'graphql.macro';
import moment from 'moment';
import {
    AnswerOption,
    Maybe,
    MultiselectConfig,
    PageNode,
    PageProgressNode,
    ProgramNode,
    UserInput,
} from 'src/generated/webapp_gql';
import { PageType } from './models/program';

/**
 * A fragment to fetch the "current" page and its
 * selected options.
 */
export const CurrentPageFragment = gql`
    fragment currentPage on PageNode {
        id
        type
        parsedAnswerOptions @client {
            isSelected
            customOptionValue
        }
    }
`;

/**
 *
 * @param node a PageProgressNode
 * @returns the stored input for the page
 */
function parseInput(node: PageProgressNode): Maybe<UserInput[]> {
    try {
        const { input } = node;

        if (!input) {
            return null;
        }

        const parsedInput = JSON.parse(input);
        if (!Array.isArray(parsedInput)) {
            return null;
        }

        return (
            parsedInput
                // Only map objects
                .filter(answer => typeof answer === 'object')
                // Normalize to the correct UserInput structure
                .map(
                    (userInput): UserInput => ({
                        __typename: 'UserInput' as const,
                        textValue: userInput?.textValue ?? null,
                        optionValue: userInput?.optionValue ?? null,
                    }),
                ) ?? null
        );
    } catch (error) {
        Sentry.captureException(error);

        return null;
    }
}

function findCurrentPageProgress(node: PageNode): Maybe<PageProgressNode> {
    if (node.pageProgress.length === 0) {
        return null;
    }
    const currentPageProgress = node.pageProgress?.reduce((a, b) =>
        !a.completedAt || (b.completedAt && moment(b.completedAt).isAfter(moment(a.completedAt)))
            ? b
            : a,
    );

    if (!currentPageProgress) {
        return null;
    }

    return {
        ...currentPageProgress,
        parsedInput: parseInput(currentPageProgress),
    };
}

/**
 * A simple type guard that handles object typing.
 *
 * @param value any value
 * @returns whether the value is an object and non-null.
 */
function isRecord(value: unknown): value is Record<string, unknown> {
    if (typeof value === 'object' && value !== null) {
        // Some type assertions...

        // This should not error because
        // casting between Record generics
        // is never an error.
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        value as Record<string, unknown>;

        const _recordAny: Record<string, any> = value;
        // This should error because TypeScript doesn't
        // allow assigning object types to Record<string, unknown>

        // @ts-expect-error Value should not be assignable to Record<string, unknown>
        const _recordUnknown: Record<string, unknown> = value;

        // This should error because we've narrowed the value as non-null.

        // @ts-expect-error Value should not be assignable to null
        const _badNull: null = value;

        return true;
    }

    return false;
}

export default {
    ProgramNode: {
        isCompleted: (node: ProgramNode): boolean => {
            return node?.programProgress?.some(progress => progress.completedAt) || false;
        },
    },
    PageNode: {
        currentPageProgress: (node: PageNode): Maybe<PageProgressNode> => {
            try {
                return findCurrentPageProgress(node);
            } catch (error) {
                Sentry.captureException(error);
                return null;
            }
        },
        parsedAnswerOptions: (node: PageNode): Maybe<AnswerOption[]> => {
            try {
                let { answerOptions } = node;

                if (!answerOptions) {
                    if (node.type === PageType.QuestionInput) {
                        // Inject an empty answer if the page
                        // is a text input page. Text input pages
                        // use the 0-index answer to store their input.
                        // we need to set text to empty string to find in pageProgress saved value
                        answerOptions = '[{text: ""}]';
                    } else {
                        return null;
                    }
                }

                const parsedAnswerOptions = JSON.parse(answerOptions) as unknown;
                let answerOptionsArray: unknown[];

                if (!Array.isArray(parsedAnswerOptions)) {
                    if (node.type === PageType.QuestionInput) {
                        // See above, inject an empty
                        // answer to text input pages.
                        answerOptionsArray = [{ text: '' }];
                    } else {
                        return null;
                    }
                } else {
                    answerOptionsArray = parsedAnswerOptions;
                }

                if (node.type === PageType.QuestionInput && answerOptionsArray.length === 0) {
                    // See above, inject an empty
                    // answer to text input pages.
                    answerOptionsArray = [{ text: '' }];
                }

                const currentPageProgress = findCurrentPageProgress(node);

                return answerOptionsArray.map((answer: unknown) => {
                    const parsedAnswer: Record<string, unknown> = isRecord(answer) ? answer : {};

                    const parsedInput = currentPageProgress?.parsedInput?.find(
                        input => input && input.optionValue === parsedAnswer.text,
                    );

                    let parsedCustomOptions: Record<string, unknown> | null;
                    if ('customOptions' in parsedAnswer && isRecord(parsedAnswer.customOptions)) {
                        parsedCustomOptions = parsedAnswer.customOptions;
                    } else {
                        parsedCustomOptions = null;
                    }

                    return {
                        // Field defaults
                        text: null,
                        // Parsed fields
                        ...parsedAnswer,
                        isSelected: parsedAnswer.text != null && !!parsedInput,
                        customOptionValue: parsedInput?.textValue ?? null,
                        // Because this is nested, we handle it after the parsed
                        // fields.
                        customOptions: !parsedCustomOptions
                            ? null
                            : {
                                  // Field defaults
                                  title: null,
                                  placeholder: null,
                                  buttonText: null,
                                  // Parsed fields
                                  ...parsedCustomOptions,
                                  // Required field overrides for GraphQL
                                  __typename: 'AnswerCustomOptions',
                              },
                        // Required field overrides for GraphQL
                        __typename: 'AnswerOption',
                    };
                });
            } catch (error) {
                Sentry.captureException(error);

                return null;
            }
        },
        parsedMultiselectConfig: (node: PageNode): Maybe<MultiselectConfig> => {
            try {
                const { multiselectConfig } = node;

                if (!multiselectConfig) {
                    return null;
                }

                const parsedMultiselectConfig = JSON.parse(multiselectConfig);

                if (!isRecord(parsedMultiselectConfig)) {
                    return null;
                }

                return {
                    // Field defaults
                    maxSelectable: null,
                    minSelectable: null,
                    // Parsed fields
                    ...parsedMultiselectConfig,
                    // Required field overrides for GraphQL
                    __typename: 'MultiselectConfig',
                };
            } catch (error) {
                Sentry.captureException(error);

                return null;
            }
        },
    },
    ProgramProgressNode: {
        parsedInput: node => parseInput(node),
    },
};
